import { match, P } from 'ts-pattern'
import { useGetIntegrationFromHalLink } from '../../utils/api-client/api-hooks.ts'
import type { RepositoryIntegration } from '../../utils/api-client/api-schemas.ts'

/** The instance a repository mapping is bound to, followed from the mapping's own HAL link. */
export const useBoundInstance = (mapping: RepositoryIntegration | undefined) =>
  useGetIntegrationFromHalLink({ path: mapping?._links.integration?.href })

export type PolarisMappingStatus = 'pending' | 'fetchable' | 'unbound' | 'unverified'

/**
 * Whether the platform can import through the repository's Polaris mapping. It fetches only from a
 * managed instance (`PolarisFetchTargetResolver`), so a mapping bound to the system instance, or
 * created from an upload before managed instances existed, cannot be imported through.
 *
 * `undefined` when the repository has no Polaris mapping. A mapping with no `_links.integration`, or
 * one whose link names no instance, is `'unbound'`. Anything else without an answer yet is `'pending'`,
 * including a request paused while the browser is offline.
 */
export const usePolarisMappingStatus = (
  mappings: RepositoryIntegration[] | undefined
): PolarisMappingStatus | undefined => {
  const mapping = mappings?.find(candidate => candidate.type === 'polaris')
  const { data: boundInstance, isPending, isError } = useBoundInstance(mapping)
  const hasLink = !!mapping?._links.integration?.href
  return match({ mapping, hasLink, isPending, isError, boundInstance })
    .with({ mapping: undefined }, () => undefined)
    .with({ hasLink: false }, () => 'unbound' as const)
    .with({ isError: true }, () => 'unverified' as const)
    .with({ isPending: true }, () => 'pending' as const)
    .with({ boundInstance: { source: 'managed' } }, () => 'fetchable' as const)
    .with({ boundInstance: P._ }, () => 'unbound' as const)
    .exhaustive()
}
